import React from "react";
import { useTranslation } from "react-i18next";

import { Alert, Button, Popconfirm, Typography } from "antd";

import { DeleteOutlined } from "@ant-design/icons";

import { API_URL } from "../../shared/utils/Util";
import { DeleteItemSchemaResponse } from "../../types/api";
import { ItemSchema } from "../../types/model";
import { fetchT } from "../../shared/utils/TypedUtil";
import { useNotification } from "../../shared/hooks/useNotification";
import { getItemSchemaComboUsage } from "../combos/api";
import { formatCents } from "../combos/pricing";
import { ItemSchemaComboUsage } from "../combos/types";

type ItemSchemaDeleteButtonProps = {
  restaurantId: string;
  item_schema: ItemSchema;
  on_delete_success: () => void;
};

// "Burger Meal (entree), Drinks add-on ($2.00 tier)".
export function describeComboUsage(
  usage: ItemSchemaComboUsage,
  t: (key: string, options?: Record<string, unknown>) => string
): string[] {
  const entrees = (usage.entrees ?? []).map((c) =>
    t("delete_item_combo_usage_entree", { name: c.name })
  );
  const tiers = (usage.addon_tiers ?? []).map((tier) =>
    t("delete_item_combo_usage_tier", {
      addon: tier.combo_addon_schema_name,
      tier:
        tier.combo_addon_tier_name && tier.combo_addon_tier_name.trim() !== ""
          ? tier.combo_addon_tier_name
          : t("combo_tier_default_name", { price: formatCents(tier.combo_addon_tier_price) }),
    })
  );
  return [...entrees, ...tiers];
}

export const ItemSchemaDeleteButton = (props: ItemSchemaDeleteButtonProps) => {
  const { deleteSuccess, notifyError } = useNotification();
  const { t } = useTranslation();
  const [visible, setVisible] = React.useState<boolean>(false);
  const [checkingUsage, setCheckingUsage] = React.useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = React.useState<boolean>(false);
  // Where the item is used in combos; null if the lookup failed.
  const [usage, setUsage] = React.useState<string[] | null>([]);

  // Look up combo usage first so the confirmation can warn that deleting
  // the item also removes it from those combos and add-on tiers.
  const showPopconfirm = () => {
    setCheckingUsage(true);
    getItemSchemaComboUsage(
      props.restaurantId,
      props.item_schema.id,
      (result) => {
        setUsage(describeComboUsage(result, t));
        setCheckingUsage(false);
        setVisible(true);
      },
      () => {
        setUsage(null);
        setCheckingUsage(false);
        setVisible(true);
      }
    );
  };

  const handleOk = () => {
    setConfirmLoading(true);

    fetchT<DeleteItemSchemaResponse>(
      "DELETE",
      `${API_URL}/v1/r/${props.restaurantId}/item_schemas/${props.item_schema.id}`,
      {},
      (result: DeleteItemSchemaResponse) => {
        props.on_delete_success();
        deleteSuccess(props.item_schema.name);
        setConfirmLoading(false);
        setVisible(false);
      },
      (err: unknown) => {
        setConfirmLoading(false);
        setVisible(false);
        notifyError(t("error"), err);
      }
    );
  };

  const handleCancel = () => {
    setVisible(false);
  };

  const description = (
    <div style={{ maxWidth: 360 }}>
      <Typography.Text>{t("delete_menu_item_description")}</Typography.Text>
      {usage === null ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 8 }}
          message={t("delete_item_combo_usage_unknown")}
        />
      ) : usage.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 8 }}
          data-testid="delete-item-combo-usage"
          message={t("delete_item_used_in_combos", { list: usage.join(", ") })}
        />
      ) : null}
    </div>
  );

  return (
    <Popconfirm
      title={t("delete_menu_item_confirm", { name: props.item_schema.name })}
      description={description}
      open={visible}
      onConfirm={handleOk}
      okButtonProps={{ loading: confirmLoading }}
      okText={t("yes")}
      cancelText={t("no")}
      onCancel={handleCancel}
    >
      <Button
        shape="circle"
        icon={<DeleteOutlined />}
        loading={checkingUsage}
        onClick={showPopconfirm}
      />
    </Popconfirm>
  );
};
